// asset-type.service.ts
import { Injectable } from '@angular/core';
import { EaActifEnDirectFormComponent } from '@app/forms/assets/ea-actif-en-direct-form/ea-actif-en-direct-form.component';
import { EaAssurancePrevoyanceFormComponent } from '@app/forms/assets/ea-assurance-prevoyance-form/ea-assurance-prevoyance-form.component';
import { EaAutresFormComponent } from '@app/forms/assets/ea-autres-form/ea-autres-form.component';
import { EaAvFormComponent } from '@app/forms/assets/ea-av-form/ea-av-form.component';
import { EaBankResourcesFormComponent } from '@app/forms/assets/ea-bank-resources-form/ea-bank-resources-form.component';
import { EaCompteCryptoFormComponent } from '@app/forms/assets/ea-compte-crypto-form/ea-compte-crypto-form.component';
import { EaCompteTitresFormComponent } from '@app/forms/assets/ea-compte-titres-form/ea-compte-titres-form.component';
import { EaContratCapiFormComponent } from '@app/forms/assets/ea-contrat-capi-form/ea-contrat-capi-form.component';
import { EaCrowdfundingFormComponent } from '@app/forms/assets/ea-crowdfunding-form/ea-crowdfunding-form.component';
import { EaDefiscFormComponent } from '@app/forms/assets/ea-defisc-form/ea-defisc-form.component';
import { EaPeaFormComponent } from '@app/forms/assets/ea-pea-form/ea-pea-form.component';
import { EaPeaPmeFormComponent } from '@app/forms/assets/ea-pea-pme-form/ea-pea-pme-form.component';
import { EaPeeFormComponent } from '@app/forms/assets/ea-pee-form/ea-pee-form.component';
import { EaPeiFormComponent } from '@app/forms/assets/ea-pei-form/ea-pei-form.component';
import { EaPerFormComponent } from '@app/forms/assets/ea-per-form/ea-per-form.component';
import { EaPercoFormComponent } from '@app/forms/assets/ea-perco-form/ea-perco-form.component';
import { EaPerpFormComponent } from '@app/forms/assets/ea-perp-form/ea-perp-form.component';
import { EaPmeFormComponent } from '@app/forms/assets/ea-pme-form/ea-pme-form.component';
import { EaPrefonFormComponent } from '@app/forms/assets/ea-prefon-form/ea-prefon-form.component';
import { EaTangibleFormComponent } from '@app/forms/assets/ea-tangible-form/ea-tangible-form.component';
import { AssetCategory } from '@app/models/assetCategory';
import { AssetTypeOption } from '@app/models/assetTypeOption';

@Injectable({
  providedIn: 'root',
})
export class AssetTypeService {
  private readonly categories: AssetCategory[] = [
    {
      id: 'TANGIBLE',
      label: 'Actif tangible',
      form: null,
      url: '/dashboard/assets/tangible',
    },
    {
      id: 'OTHERS',
      label: 'Diversification',
      form: null,
      url: '/dashboard/assets/diversification',
    },
    {
      id: 'BANKING',
      label: 'Bancaire',
      form: null,
      url: '/dashboard/assets/bank-resources',
    },
    this.getCrowdfunding(),
    {
      id: 'TAX_EXEMPTING',
      label: 'Défiscalisant',
      form: null,
      url: '/dashboard/assets/defiscalisation',
    },
    { id: 'FINANCIAL', label: 'Financier', form: null },
    {
      id: 'PME',
      label: 'Private Equity',
      form: EaPmeFormComponent,
      url: '/dashboard/assets/pme',
    },
  ];

  getCategories(): AssetCategory[] {
    return this.categories;
  }

  getCategoriesForNestedSelect(): {
    label: string;
    value?: string;
    subcategories?: { label: string; value: string }[];
  }[] {
    return this.categories.map(category => {
      const subcategories = this.getTypeOptionsFromCategoryId(category.id).map(
        option => ({
          label: option.label,
          value: option.id,
        }),
      );

      if (subcategories.length === 0) {
        return {
          label: category.label,
          value: category.id,
        };
      }

      return {
        label: category.label,
        subcategories: subcategories,
      };
    });
  }

  getTypeOptionsFromCategoryLabel(label: string): AssetTypeOption[] {
    const category = this.getCategoryByLabel(label);
    return category ? this.getTypeOptionsFromCategoryId(category.id) : [];
  }

  getTypeOptionsFromCategory(label: string): AssetTypeOption[] {
    return this.getTypeOptionsFromCategoryLabel(label);
  }

  getCategoryById(id: string): AssetCategory | undefined {
    return this.categories.find(category => category.id === id);
  }

  getCategoryByLabel(label: string): AssetCategory | undefined {
    return this.categories.find(category => category.label === label);
  }

  getCategoryByAssetTypeOptionId(typeId: string): AssetCategory | undefined {
    return this.categories.find(category => {
      const options = this.getTypeOptionsFromCategoryId(category.id);
      return options.some(option => option.id === typeId);
    });
  }

  getCategoryByAssetTypeOption(
    option: AssetTypeOption,
  ): AssetCategory | undefined {
    return this.getCategoryByAssetTypeOptionId(option.id);
  }

  getAssetTypeOptionById(id: string): AssetTypeOption | undefined {
    const allOptions = [
      ...this.getBankingAssetTypeOptions(),
      ...this.getFinancialAssetTypeOptions(),
      ...this.getTaxExemptingAssetTypeOptions(),
      ...this.getPrivateEquityOptions(),
      ...this.getTangibleAssetTypeOptions(),
      ...this.getInsurancesAssetsTypeOptions(),
      ...this.getOthersAssetsTypeOptions(),
    ];
    return allOptions.find(option => option.id === id);
  }

  getAssetTypeOptionIdsByCategory(category: AssetCategory): string[] {
    return this.getTypeOptionsFromCategoryId(category.id).map(
      option => option.id,
    );
  }

  getTypeOptionsFromCategoryId(categoryId: string): AssetTypeOption[] {
    const categoryMap: Record<string, () => AssetTypeOption[]> = {
      BANKING: this.getBankingAssetTypeOptions.bind(this),
      FINANCIAL: this.getFinancialAssetTypeOptions.bind(this),
      TAX_EXEMPTING: () =>
        this.getTaxExemptingAssetTypeOptions().filter(t => t.id !== 'PME'),
      PME: this.getPrivateEquityOptions?.bind(this) || (() => []),
      PRIVATE_EQUITY: this.getPrivateEquityOptions?.bind(this) || (() => []),
      CROWDFUNDING: this.getCrowdfundingOptions?.bind(this) || (() => []),
      TANGIBLE: this.getTangibleAssetTypeOptionsSorted.bind(this),
      INSURANCE: this.getInsurancesAssetsTypeOptions.bind(this),
      OTHERS: this.getOthersAssetsTypeOptions.bind(this),
    };

    return categoryMap[categoryId]?.() || [];
  }

  getBankingAssetTypeOptions(): AssetTypeOption[] {
    return [
      { id: 'CEL', label: 'CEL', form: EaBankResourcesFormComponent },
      { id: 'CODEVI', label: 'CODEVI', form: EaBankResourcesFormComponent },
      {
        id: 'CAT',
        label: 'Compte à terme',
        form: EaBankResourcesFormComponent,
      },
      {
        id: 'COMPTE_COURANT',
        label: 'Compte courant',
        form: EaBankResourcesFormComponent,
      },
      {
        id: 'CSL',
        label: 'Compte sur livret',
        form: EaBankResourcesFormComponent,
      },
      { id: 'LDD', label: 'LDD', form: EaBankResourcesFormComponent },
      { id: 'LIVRET', label: 'Livret', form: EaBankResourcesFormComponent },
      { id: 'LIVRET_A', label: 'Livret A', form: EaBankResourcesFormComponent },
      { id: 'LIVRET_B', label: 'Livret B', form: EaBankResourcesFormComponent },
      {
        id: 'SOCIAL_SHARE',
        label: 'Parts sociales',
        form: EaBankResourcesFormComponent,
      },
      { id: 'PEL', label: 'PEL', form: EaBankResourcesFormComponent },
    ];
  }

  getFinancialAssetTypeOptions(): AssetTypeOption[] {
    return [
      {
        id: 'ACTIF_EN_DIRECT',
        label: 'Actif en direct',
        form: EaActifEnDirectFormComponent,
      },
      { id: 'ASSURANCE_VIE', label: 'Assurance vie', form: EaAvFormComponent },
      {
        id: 'COMPTE_TITRE',
        label: 'Compte titre',
        form: EaCompteTitresFormComponent,
        url: '/dashboard/assets/comptes-titres',
      },
      {
        id: 'COMPTE_CRYPTO',
        label: 'Compte crypto',
        form: EaCompteCryptoFormComponent,
        url: '/dashboard/assets/comptes-cryptos',
      },
      {
        id: 'CONTRAT_CAPI',
        label: 'Contrat de capitalisation',
        form: EaContratCapiFormComponent,
        url: '/dashboard/assets/contrat-capi',
      },
      {
        id: 'PEA',
        label: 'PEA',
        form: EaPeaFormComponent,
        url: '/dashboard/assets/pea',
      },
      {
        id: 'PEA_PME',
        label: 'PEA PME',
        form: EaPeaPmeFormComponent,
        url: '/dashboard/assets/pea',
      },
      {
        id: 'PEE',
        label: 'PEE',
        form: EaPeeFormComponent,
        url: '/dashboard/assets/pee',
      },
      { id: 'PEI', label: 'PEI', form: EaPeiFormComponent },
      {
        id: 'PER',
        label: 'PER',
        form: EaPerFormComponent,
        url: '/dashboard/assets/per',
      },
      {
        id: 'PERCO',
        label: 'PERCO',
        form: EaPercoFormComponent,
        url: '/dashboard/assets/pee',
      },
      {
        id: 'PERP',
        label: 'PERP',
        form: EaPerpFormComponent,
        url: '/dashboard/assets/per',
      },
      {
        id: 'PREFON',
        label: 'PREFON',
        form: EaPrefonFormComponent,
        url: '/dashboard/assets/prefon',
      },
    ];
  }

  getTaxExemptingAssetTypeOptions(): AssetTypeOption[] {
    return [
      {
        id: 'FCPI',
        label: 'FCPI',
        form: EaDefiscFormComponent,
        url: '/dashboard/assets/defiscalisation',
      },
      {
        id: 'FIP',
        label: 'FIP',
        form: EaDefiscFormComponent,
        url: '/dashboard/assets/defiscalisation',
      },
      {
        id: 'FIP_CORSE',
        label: 'FIP Corse ',
        form: EaDefiscFormComponent,
        url: '/dashboard/assets/defiscalisation',
      },
      {
        id: 'FIP_DOMTOM',
        label: 'FIP DOM-TOM',
        form: EaDefiscFormComponent,
        url: '/dashboard/assets/defiscalisation',
      },
      {
        id: 'GFF',
        label: 'GFF',
        form: EaDefiscFormComponent,
        url: '/dashboard/assets/defiscalisation',
      },
      {
        id: 'GFI',
        label: 'GFI',
        form: EaDefiscFormComponent,
        url: '/dashboard/assets/defiscalisation',
      },
      {
        id: 'GFV',
        label: 'GFV',
        form: EaDefiscFormComponent,
        url: '/dashboard/assets/defiscalisation',
      },
      this.getGirardin(),
      this.getSofica(),
    ];
  }

  getTaxExemptingAssetTypeFipFCPI(): string[] {
    return ['FIP', 'FCPI', 'FIP_CORSE', 'FIP_DOMTOM', 'GFI', 'GFF', 'GFV'];
  }

  getPrivateEquityFundTypes(): string[] {
    return ['FPCI', 'FPCR', 'FCPR', 'FPS'];
  }

  getPrivateEquityOptions(): AssetTypeOption[] {
    return [
      { id: 'PME', label: 'Private Equity', form: EaPmeFormComponent },
      ...this.getPrivateEquityFundTypes().map(id => ({
        id,
        label: id,
        form: EaAutresFormComponent,
      })),
    ];
  }

  getCrowdfundingOptions(): AssetTypeOption[] {
    return [];
  }

  getTangibleAssetTypeOptionsSorted(): AssetTypeOption[] {
    return this.getTangibleAssetTypeOptions().sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
    );
  }

  getTangibleAssetTypeOptions(): AssetTypeOption[] {
    return [
      { id: 'CRYPTO', label: 'Crypto-monnaie', form: EaTangibleFormComponent },
      {
        id: 'BOND',
        label: 'Fonds obligataires',
        form: EaTangibleFormComponent,
      },
      { id: 'FOREST', label: 'Forêt', form: EaTangibleFormComponent },
      {
        id: 'EXISTING_HOUSING',
        label: 'Immobilier ancien nu',
        form: EaTangibleFormComponent,
      },
      {
        id: 'JEWELLERY',
        label: 'Montre / Bijou',
        form: EaTangibleFormComponent,
      },
      { id: 'NFT', label: 'NFT', form: EaTangibleFormComponent },
      {
        id: 'COLLECTION',
        label: 'Objet de collection',
        form: EaTangibleFormComponent,
      },
      {
        id: 'PIECE_OF_ART',
        label: "Œuvre d'art",
        form: EaTangibleFormComponent,
      },
      { id: 'OPCI', label: 'OPCI', form: EaTangibleFormComponent },
      { id: 'GOLD', label: 'Or physique', form: EaTangibleFormComponent },
      { id: 'SOLAR', label: 'Solaire', form: EaTangibleFormComponent },
      {
        id: 'AGRICULTURAL_LAND',
        label: 'Terre agricole',
        form: EaTangibleFormComponent,
      },
      { id: 'VIGNOBLE', label: 'Vignoble', form: EaTangibleFormComponent },
      { id: 'WINE', label: 'Vin', form: EaTangibleFormComponent },
    ];
  }

  getInsurancesAssetsTypeOptions(): AssetTypeOption[] {
    return [
      {
        id: 'BORROWER_INSURANCE',
        label: 'Assurance emprunteur',
        form: EaAssurancePrevoyanceFormComponent,
      },
      {
        id: 'INSURANCE',
        label: 'Mutuelle',
        form: EaAssurancePrevoyanceFormComponent,
      },
      {
        id: 'CONTINGENCY',
        label: 'Prévoyance',
        form: EaAssurancePrevoyanceFormComponent,
      },
    ];
  }

  getOthersAssetsTypeOptions(): AssetTypeOption[] {
    return [
      {
        id: 'DIVERSIFICATION_INVESTMENT',
        label: 'Investissement de diversification',
        form: EaAutresFormComponent,
      },
      {
        id: 'TITRES_PARTICIPATIFS',
        label: 'Titres participatifs',
        form: EaAutresFormComponent,
      },
      {
        id: 'OBLIGATIONS',
        label: 'Obligations',
        form: EaAutresFormComponent,
      },
    ];
  }

  getCrowdfunding(): AssetCategory {
    return {
      id: 'CROWDFUNDING',
      label: 'Crowdfunding',
      form: EaCrowdfundingFormComponent,
      url: '/dashboard/assets/crowdfunding',
    };
  }

  getGirardin(): AssetTypeOption {
    return {
      id: 'GIRARDIN',
      label: 'GIRARDIN',
      form: EaDefiscFormComponent,
      url: '/dashboard/assets/defiscalisation',
    };
  }

  getSofica(): AssetTypeOption {
    return {
      id: 'SOFICA',
      label: 'SOFICA',
      form: EaDefiscFormComponent,
      url: '/dashboard/assets/defiscalisation',
    };
  }
}
